import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { saveAccountId, saveAllAccounts, useAuth, useLazyGetPersonAccessesQuery } from '@ibk/shared-auth';
import { setSelectedAccountId, useAppDispatch, useGetProductRelationShips } from '@ibk/shared-config';
import { maskEmail, maskPhone, saveLastAccessedCompany } from '@ibk/shared-utils';
import { useContabilCompanyLinkRedirect } from '../ContabilCompanyLink/hooks/useContabilCompanyLinkRedirect';
export function useTwoFactorVerify(state) {
    const [localError, setLocalError] = useState(null);
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { validateToken, sendToken, loading: authLoading, error: authError } = useAuth();
    const { saveBillingProduct } = useGetProductRelationShips();
    const [getPersonAccesses, { isLoading: companiesLoading }] = useLazyGetPersonAccessesQuery();
    const { resolveDestination } = useContabilCompanyLinkRedirect();
    const method = state?.method || 'phone';
    const getMaskedRecipient = () => {
        if (!state?.maskedRecipient) {
            return method === 'phone' ? '(**) *****-****' : '********@*mail.com';
        }
        return method === 'phone' ? maskPhone(state.maskedRecipient) : maskEmail(state.maskedRecipient);
    };
    const maskedRecipient = getMaskedRecipient();
    useEffect(() => {
        const hasRequiredData = state?.method && state?.maskedRecipient;
        if (!hasRequiredData) {
            navigate('/two-factor-select', { replace: true });
        }
    }, [state, navigate]);
    const handleContinue = async (token) => {
        setLocalError(null);
        let isTokenValid = false;
        try {
            isTokenValid = await validateToken(token);
        }
        catch (err) {
            setLocalError('Erro ao validar token. Tente novamente.');
            return;
        }
        if (!isTokenValid) {
            setLocalError('Token inválido. Verifique o código e tente novamente.');
            return;
        }
        try {
            const response = await getPersonAccesses().unwrap();
            const companies = response?.result || [];
            if (companies.length === 1) {
                const singleCompany = companies[0];
                const companyId = singleCompany.targetId;
                dispatch(setSelectedAccountId(companyId));
                saveAllAccounts(companies);
                saveAccountId(companies, companyId);
                await saveBillingProduct(companyId);
                saveLastAccessedCompany(companyId);
                const destination = await resolveDestination(singleCompany.entity?.id);
                navigate(destination, { replace: true });
                return;
            }
            navigate('/company-selection', {
                replace: true,
                state: { from: 'two-factor-verify' },
            });
        }
        catch (err) {
            navigate('/company-selection', {
                replace: true,
                state: { from: 'two-factor-verify' },
            });
        }
    };
    const handleBack = () => {
        if (!authLoading && !companiesLoading) {
            navigate('/two-factor-select', { replace: true });
        }
    };
    const handleResend = async () => {
        setLocalError(null);
        try {
            await sendToken(method);
        }
        catch (err) {
            setLocalError('Erro ao reenviar token. Tente novamente.');
        }
    };
    const displayError = localError || authError;
    return {
        method,
        maskedRecipient,
        error: displayError,
        disabled: authLoading || companiesLoading,
        onResend: handleResend,
        onContinue: handleContinue,
        onBack: handleBack,
    };
}
